import TextField, { TextFieldProps } from '@mui/material/TextField';
import { typedMemo } from '@utils';
import { useMemo } from 'react';
import { Controller, FieldPath, FieldValues } from 'react-hook-form';
import { BaseControllerProps } from './types';
import { useFormFieldValidation } from './useFormFieldValidation';

interface TextFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<
      TextFieldProps,
      'name' | 'value' | 'onChange' | 'error' | 'helperText'
    >,
    BaseControllerProps<TFieldValues, TName> {}

export const TextFormField = typedMemo(
  <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  >({
    name,
    control,
    rules,
    required,
    ...textFieldProps
  }: Readonly<TextFormFieldProps<TFieldValues, TName>>) => {
    const validationRules = useFormFieldValidation<TFieldValues, TName>(
      required,
      rules,
    );

    const textFieldPropsWithDefaults = useMemo(
      () => ({
        size: 'small' as const,
        margin: 'dense' as const,
        fullWidth: true,
        ...textFieldProps,
      }),
      [textFieldProps],
    );

    return (
      <Controller
        name={name}
        control={control}
        rules={validationRules}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            value={field.value ?? ''}
            {...textFieldPropsWithDefaults}
            required={required}
            error={!!error}
            helperText={error?.message || ' '}
          />
        )}
      />
    );
  },
);
